import type { ReactNode } from "react";

import { cn } from "@/lib/utils";
import "@/styles/list-skeleton.css";

export interface MobileRecordCardProps {
  /** Main line (name, number, title…). */
  title: ReactNode;
  /** Secondary line under the title (description, company…). */
  subtitle?: ReactNode;
  /** Small facts shown on the last line, separated by dots. */
  meta?: ReactNode[];
  /** Top-right slot: a status badge, an amount… */
  trailing?: ReactNode;
  /** Leading slot: an avatar or icon. */
  leading?: ReactNode;
  className?: string;
}

/**
 * Compact glass card that stands in for a table row on phones, where a
 * multi-column table would overflow horizontally. Purely presentational: the
 * tap target is provided by the list rendering it (DataTable `mobileCard`).
 */
export const MobileRecordCard = ({
  title,
  subtitle,
  meta,
  trailing,
  leading,
  className,
}: MobileRecordCardProps) => {
  const facts = (meta ?? []).filter(
    (fact) => fact != null && fact !== false && fact !== "",
  );
  return (
    <div className={cn("flex items-start gap-3", className)}>
      {leading && <div className="shrink-0 pt-0.5">{leading}</div>}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-start justify-between gap-3">
          <div className="line-clamp-2 min-w-0 flex-1 font-medium leading-snug break-words">
            {title}
          </div>
          {trailing && <div className="shrink-0">{trailing}</div>}
        </div>
        {subtitle && (
          <div className="line-clamp-2 text-sm text-muted-foreground">
            {subtitle}
          </div>
        )}
        {facts.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-muted-foreground">
            {facts.map((fact, index) => (
              <span key={index} className="inline-flex items-center gap-1.5">
                {index > 0 && <span aria-hidden>·</span>}
                {fact}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
